import React, { useState, useRef } from 'react';
import Papa from 'papaparse';
import {
  Box, Button, Typography, Alert, Chip, CircularProgress,
  Divider, ToggleButton, ToggleButtonGroup,
  Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import { Upload, Download, Search } from '@mui/icons-material';
import { format, parseISO, addDays, addWeeks, endOfMonth } from 'date-fns';
import { getAllBookings } from '../../services/bookingService';

// ── constants ───────────────────────────────────────────────────────────────

// How many minutes either side we still consider a "full match"
const TOLERANCE_MINS = 30;

const SESSION_TIMES = {
  am:      { start: 8 * 60 + 30,  end: 12 * 60 + 30 }, // 08:30–12:30
  pm:      { start: 13 * 60 + 30, end: 17 * 60 + 30 }, // 13:30–17:30
  all_day: { start: 8 * 60 + 30,  end: 17 * 60 + 30 }, // 08:30–17:30
};

const STATUS = {
  NO_SESSION: { label: 'No clinic session found',          color: 'error'   },
  PARTIAL:    { label: 'Clinic ran – period not fully used', color: 'warning' },
  FULL:       { label: 'Full match',                        color: 'success' },
};

// ── helpers ─────────────────────────────────────────────────────────────────

function parseUKDate(str) {
  if (!str) return null;
  const s = String(str).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
    const [d, m, y] = s.split('/');
    return `${y}-${m}-${d}`;
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.substring(0, 10);
  return null;
}

function toMins(timeStr) {
  if (!timeStr) return null;
  const s = String(timeStr).trim();
  // handles "08:30", "8:30", "08:30:00"
  const match = s.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  return parseInt(match[1]) * 60 + parseInt(match[2]);
}

function fmtTime(mins) {
  if (mins == null) return '—';
  return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
}

function normalise(s) {
  return (s || '').trim().toUpperCase();
}

// Safe date formatter — works on "2026-03-20", "2026-03-20T00:00:00", etc.
function fmtDate(raw) {
  if (!raw) return '—';
  const iso = String(raw).substring(0, 10); // always "YYYY-MM-DD"
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function dayName(raw) {
  if (!raw) return '';
  const iso = String(raw).substring(0, 10);
  try { return format(parseISO(iso), 'EEE'); } catch { return ''; }
}

function endDateForScope(startDate, scope) {
  const d = parseISO(startDate);
  if (scope === '1w')    return format(addDays(addWeeks(d, 1), -1), 'yyyy-MM-dd');
  if (scope === '2w')    return format(addDays(addWeeks(d, 2), -1), 'yyyy-MM-dd');
  if (scope === 'month') return format(endOfMonth(d), 'yyyy-MM-dd');
  return startDate;
}

function classifyBooking(booking, csvMatches) {
  if (csvMatches.length === 0) return { key: 'NO_SESSION', csvStart: null, csvEnd: null };

  const sessionTimes = SESSION_TIMES[booking.session] || SESSION_TIMES.all_day;

  // Filter CSV rows by session using end time as the discriminator:
  // AM rows end before 13:30, PM rows start at or after 12:00, all_day keeps everything.
  const MIDDAY = 12 * 60;          // 12:00
  const PM_START_CUTOFF = 13 * 60 + 30; // 13:30

  const sessionRows = csvMatches.filter(r => {
    const startT = toMins(r.start_time);
    const endT   = toMins(r.end_time);
    if (booking.session === 'am') {
      // Keep only rows whose end time is before 13:30 (i.e. genuinely AM)
      if (endT != null && endT > PM_START_CUTOFF) return false;
      return true;
    }
    if (booking.session === 'pm') {
      // Keep only rows whose start time is at or after midday
      if (startT != null && startT < MIDDAY) return false;
      return true;
    }
    return true; // all_day — include all
  });

  const relevantRows = sessionRows.length > 0 ? sessionRows : csvMatches;

  const starts = relevantRows.map(r => toMins(r.start_time)).filter(v => v != null);
  const ends   = relevantRows.map(r => toMins(r.end_time)).filter(v => v != null);

  if (starts.length === 0 || ends.length === 0) {
    return { key: 'PARTIAL', csvStart: null, csvEnd: null };
  }

  const csvFirst = Math.min(...starts);
  const csvLast  = Math.max(...ends);

  const startOk = Math.abs(csvFirst - sessionTimes.start) <= TOLERANCE_MINS;
  const endOk   = Math.abs(csvLast  - sessionTimes.end)   <= TOLERANCE_MINS;

  return {
    key:      startOk && endOk ? 'FULL' : 'PARTIAL',
    csvStart: csvFirst,
    csvEnd:   csvLast,
  };
}

function downloadCSV(rows) {
  const headers = [
    'Date', 'Day', 'Status', 'Specialty', 'Room', 'Clinic', 'Doctor', 'Session',
    'Booked Start', 'Booked End', 'First Patient', 'Last Patient',
  ];
  const lines = [
    headers.join(','),
    ...rows.map(r => [
      fmtDate(r.booking_date),
      dayName(r.booking_date),
      STATUS[r.status_key]?.label || '',
      r.specialty || '',
      r.room_name || '',
      r.clinic_name || '',
      r.doctor_name || '',
      r.session || '',
      fmtTime(SESSION_TIMES[r.session]?.start),
      fmtTime(SESSION_TIMES[r.session]?.end),
      fmtTime(r.csv_start),
      fmtTime(r.csv_end),
    ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `utilisation-${format(new Date(), 'yyyy-MM-dd')}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── component ────────────────────────────────────────────────────────────────

export default function UtilisationReport() {
  const fileRef = useRef();
  const [csvRows,    setCsvRows]    = useState(null);
  const [csvFile,    setCsvFile]    = useState('');
  const [csvError,   setCsvError]   = useState('');
  const [scope,      setScope]      = useState('1w');
  const [startDate,  setStartDate]  = useState('');
  const [filter,     setFilter]     = useState('all');
  const [results,    setResults]    = useState(null);
  const [running,    setRunning]    = useState(false);
  const [runError,   setRunError]   = useState('');

  // ── upload ──────────────────────────────────────────────────────────────

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCsvError(''); setCsvRows(null); setCsvFile(file.name);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: ({ data, meta }) => {
        const headers = meta.fields || [];

        const col = (pattern) => headers.find(h => pattern.test(h.trim()));
        const dateCol      = col(/^date$/i);
        const specialtyCol = col(/^specialty$/i);
        const startCol     = col(/session.?start/i);
        const endCol       = col(/session.?end/i);
        const providerCol  = col(/^provider$/i);

        if (!dateCol || !specialtyCol) {
          setCsvError(
            `Required columns not found. Expected "Date" and "Specialty". ` +
            `Found: ${headers.join(', ')}`
          );
          return;
        }

        const parsed = data
          .map(row => ({
            date:       parseUKDate(row[dateCol]),
            specialty:  normalise(row[specialtyCol]),
            start_time: (row[startCol]    || '').trim(),
            end_time:   (row[endCol]      || '').trim(),
            provider:   (row[providerCol] || '').trim(),
          }))
          .filter(r => r.date && r.specialty);

        if (!parsed.length) {
          setCsvError('No valid rows found. Check the file format.');
          return;
        }

        const earliest = [...parsed].sort((a, b) => a.date.localeCompare(b.date))[0].date;
        setStartDate(earliest);
        setCsvRows(parsed);
      },
      error: err => setCsvError(`Parse error: ${err.message}`),
    });
    e.target.value = '';
  };

  // ── run ─────────────────────────────────────────────────────────────────

  const handleRun = async () => {
    if (!csvRows || !startDate) return;
    setRunning(true); setRunError(''); setResults(null);

    try {
      const endDate = endDateForScope(startDate, scope);

      // Determine the specialties present in the CSV so we can filter bookings
      const csvSpecialties = [...new Set(csvRows.map(r => r.specialty))];

      // Build CSV lookup: "date|SPECIALTY" → rows[]
      const csvLookup = new Map();
      for (const row of csvRows) {
        if (row.date < startDate || row.date > endDate) continue;
        const key = `${row.date}|${row.specialty}`;
        if (!csvLookup.has(key)) csvLookup.set(key, []);
        csvLookup.get(key).push(row);
      }

      // Fetch all confirmed room bookings in the date range
      const bookings = await getAllBookings({ start_date: startDate, end_date: endDate, status: 'confirmed' });

      // Only include bookings whose specialty appears in the CSV
      const relevant = bookings.filter(b =>
        csvSpecialties.includes(normalise(b.specialty))
      );

      const annotated = relevant.map(booking => {
        const key        = `${booking.booking_date}|${normalise(booking.specialty)}`;
        const csvMatches = csvLookup.get(key) || [];
        const { key: statusKey, csvStart, csvEnd } = classifyBooking(booking, csvMatches);
        return { ...booking, status_key: statusKey, csv_start: csvStart, csv_end: csvEnd };
      });

      // Sort: NO_SESSION first, then PARTIAL, then FULL; within each by date
      const order = { NO_SESSION: 0, PARTIAL: 1, FULL: 2 };
      annotated.sort((a, b) =>
        (order[a.status_key] ?? 3) - (order[b.status_key] ?? 3) ||
        a.booking_date.localeCompare(b.booking_date)
      );

      setResults({ rows: annotated, startDate, endDate });
    } catch (err) {
      setRunError(err.message || 'Failed to load bookings');
    } finally {
      setRunning(false);
    }
  };

  // ── display ──────────────────────────────────────────────────────────────

  const displayRows = results?.rows.filter(r => filter === 'all' || r.status_key === filter) || [];

  const counts = results && {
    NO_SESSION: results.rows.filter(r => r.status_key === 'NO_SESSION').length,
    PARTIAL:    results.rows.filter(r => r.status_key === 'PARTIAL').length,
    FULL:       results.rows.filter(r => r.status_key === 'FULL').length,
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Room Utilisation vs Clinic Activity</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload the clinic session CSV export. For each room booking matching the CSV's
        specialty, the report checks whether a clinic session ran on that day and whether
        the first/last patient times cover the full booked period.
      </Typography>

      {/* Step 1 — Upload */}
      <Typography variant="subtitle2" gutterBottom>1. Upload clinic session CSV</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleFile} />
        <Button variant="outlined" startIcon={<Upload />} onClick={() => fileRef.current.click()}>
          Choose CSV file
        </Button>
        {csvFile && <Typography variant="body2" color="text.secondary">{csvFile}</Typography>}
        {csvRows  && <Chip label={`${csvRows.length} rows loaded`} color="success" size="small" />}
      </Box>
      {csvError && <Alert severity="error" sx={{ mb: 2 }}>{csvError}</Alert>}

      <Divider sx={{ my: 2 }} />

      {/* Step 2 — Scope */}
      <Typography variant="subtitle2" gutterBottom>2. Select scope</Typography>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 3 }}>
        <input
          type="date"
          value={startDate}
          onChange={e => setStartDate(e.target.value)}
          disabled={!csvRows}
          style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid #ccc', fontSize: '0.875rem' }}
        />
        <ToggleButtonGroup value={scope} exclusive onChange={(_, v) => v && setScope(v)} size="small">
          <ToggleButton value="1w">1 Week</ToggleButton>
          <ToggleButton value="2w">2 Weeks</ToggleButton>
          <ToggleButton value="month">Whole Month</ToggleButton>
        </ToggleButtonGroup>
        {startDate && (
          <Typography variant="body2" color="text.secondary">
            → up to {endDateForScope(startDate, scope)}
          </Typography>
        )}
      </Box>

      {/* Run */}
      <Button
        variant="contained"
        startIcon={running ? <CircularProgress size={16} color="inherit" /> : <Search />}
        onClick={handleRun}
        disabled={!csvRows || !startDate || running}
      >
        {running ? 'Running…' : 'Run Report'}
      </Button>

      {runError && <Alert severity="error" sx={{ mt: 2 }}>{runError}</Alert>}

      {/* Results */}
      {results && (
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="subtitle2">
              {results.startDate} → {results.endDate} &nbsp;|&nbsp; {results.rows.length} room bookings analysed
            </Typography>

            {[
              { key: 'NO_SESSION', color: 'error'   },
              { key: 'PARTIAL',    color: 'warning'  },
              { key: 'FULL',       color: 'success'  },
            ].map(({ key, color }) => (
              <Chip
                key={key}
                label={`${counts[key]}  ${STATUS[key].label}`}
                color={color}
                size="small"
                onClick={() => setFilter(f => f === key ? 'all' : key)}
                variant={filter === key ? 'filled' : 'outlined'}
                sx={{ cursor: 'pointer' }}
              />
            ))}

            <Box sx={{ flexGrow: 1 }} />
            <Button size="small" variant="outlined" startIcon={<Download />} onClick={() => downloadCSV(results.rows)}>
              Download CSV
            </Button>
          </Box>

          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  {['Date', 'Day', 'Status', 'Specialty', 'Room', 'Clinic', 'Doctor', 'Session',
                    'Booked Start', 'Booked End', 'First Patient', 'Last Patient']
                    .map(h => <TableCell key={h} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {displayRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No results for this filter
                    </TableCell>
                  </TableRow>
                ) : displayRows.map(row => {
                  const times = SESSION_TIMES[row.session] || SESSION_TIMES.all_day;
                  return (
                    <TableRow
                      key={row.id}
                      sx={{
                        backgroundColor:
                          row.status_key === 'NO_SESSION' ? '#ffebee' :
                          row.status_key === 'PARTIAL'    ? '#fff8e1' : 'inherit',
                      }}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(row.booking_date)}</TableCell>
                      <TableCell>{dayName(row.booking_date)}</TableCell>
                      <TableCell>
                        <Chip
                          label={STATUS[row.status_key]?.label}
                          color={STATUS[row.status_key]?.color}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{row.specialty || '—'}</TableCell>
                      <TableCell>{row.room_name || '—'}</TableCell>
                      <TableCell>{row.clinic_name || '—'}</TableCell>
                      <TableCell>{row.doctor_name || '—'}</TableCell>
                      <TableCell>{row.session === 'am' ? 'AM' : row.session === 'pm' ? 'PM' : 'All Day'}</TableCell>
                      <TableCell>{fmtTime(times.start)}</TableCell>
                      <TableCell>{fmtTime(times.end)}</TableCell>
                      <TableCell>{fmtTime(row.csv_start)}</TableCell>
                      <TableCell>{fmtTime(row.csv_end)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Box>
      )}
    </Box>
  );
}
